import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import "../approvalMaster/ApprovalMasterList.scss";
import Modal from "react-bootstrap/Modal";
import FetchData, { GetOne } from "../../providers/dataProvider";
import { useRolePermission } from "../../hooks/useRolePermission";

const avatarColors = ["#0b5fff", "#e8a33d", "#7c4dff", "#16784f", "#c34256"];
const getAvatarColor = (index) => avatarColors[index % avatarColors.length];

const getInitials = (name = "") => {
  const words = name.trim().split(/\s+/);
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[1][0]).toUpperCase();
};

const ApprovalMasterList = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { data: approval, loading } = FetchData("approval");
  const { menuRights } = useRolePermission("Approval Master");

  const [search, setSearch] = useState("");

  const [selectedApproval, setSelectedApproval] = useState(null);
  const [show, setShow] = useState(false);
  const [approvalDetails, setApprovalDetails] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [levelRows, setLevelRows] = useState([]);

  const filteredApproval = useMemo(() => {
    if (!approval) return approval;
    if (!search.trim()) return approval;
    return approval.filter((item) =>
      item?.formName?.toLowerCase().includes(search.trim().toLowerCase())
    );
  }, [approval, search]);

  useEffect(() => {
    if (!show || !selectedApproval?.id) return;

    setDetailsLoading(true);
    GetOne("approval", { id: selectedApproval.id }, (res) => {
      setApprovalDetails(res);

      const payload = res?.levels ? res : res?.data;
      const rows = (payload?.levels || []).flatMap((level) =>
        (level.approvers || []).map((approver) => ({
          approverId: approver.id,
          approverName: approver.approverName,
          levelNo: level.levelNo,
          notifyInSystem: !!approver.notifyInSystem,
          notifyByEmail: !!approver.notifyByEmail,
        }))
      );

      setLevelRows(rows);
      setDetailsLoading(false);
    });
  }, [show, selectedApproval]);

  // View modal (levels popup)
  const handleRowClick = useCallback((item) => {
    setSelectedApproval(item);
    setShow(true);
  }, []);

  const handleClose = useCallback(() => {
    setShow(false);
    setSelectedApproval(null);
    setApprovalDetails(null);
    setLevelRows([]);
  }, []);

  const handleEdit = useCallback((item, e) => {
    e?.stopPropagation();
    if (item) {
      navigate("/edit-approval-master", {
        state: { row: item, id: item?.id, approval: selectedApproval },
      });
    }
  }, [navigate]);

  const handleKeyDown = useCallback((event) => {
    if (event.key === "Enter") {
      // search already applies live via filteredApproval; Enter just blurs
      event.target.blur();
    }
  }, []);

  const removeSearch = useCallback(() => setSearch(""), []);

  const RefreshList = useCallback(() => {
    setSearch("");
    window.location.reload();
  }, []);

  return (
    <section id="approvalMasterList">
      <div className="container-fluid">
        <div className="row pt-1">
          <div className="col-xxl-12 col-xl-12 col-lg-12 col-md-12 col-sm-12 col-xs-12 p-0">
            <div className="card approval-toolbar-card">
              <div className="card-body">
                <div className="row search-area-row">
                  <div className="col-xxl-6 col-xl-6 col-lg-6 col-md-6 col-sm-6 col-xs-12">
                    <div className="approval-title-group">
                      <p>{filteredApproval?.length ?? 0} Results Found</p>
                    </div>
                  </div>
                  <div className="col-xxl-6 col-xl-6 col-lg-6 col-md-6 col-sm-6 col-xs-12">
                    <div className="approval-command-bar">
                      <div className="approval-search-suite">
                        <div className="approval-search-control input-group">
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Search approval"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            onKeyDown={handleKeyDown}
                          />
                          <div className="input-group-append">
                            {search && (
                              <button className="btn" type="button" onClick={removeSearch} title="Clear">
                                <i className="fas fa-times"></i>
                              </button>
                            )}
                            <button className="btn" type="button" title="Search">
                              <i className="fa fa-search"></i>
                            </button>
                          </div>
                        </div>
                      </div>

                      <button className="btn refresh-btn" type="button" onClick={RefreshList} title="Refresh">
                        <i className="fas fa-redo"></i>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="table-row">
          <div className="approval-worklist-header">
            <div>Approval</div>
            <div>Levels</div>
            <div></div>
          </div>

          {loading ? (
            Array.from({ length: 6 }).map((_, i) => (
              <div className="card approval-row approval-skeleton-row" key={i}>
                <div className="card-body">
                  <div className="approval-grid">
                    <div className="name-cell">
                      <span className="skeleton-block skeleton-avatar" />
                      <span className="skeleton-block skeleton-text" />
                    </div>
                    <div>
                      <span className="skeleton-block skeleton-text short" />
                    </div>
                    <div className="open-cell"></div>
                  </div>
                </div>
              </div>
            ))
          ) : filteredApproval?.length > 0 ? (
            filteredApproval.map((item, index) => (
              <div
                className={`card approval-row ${index % 2 === 0 ? "row-even" : "row-odd"}`}
                key={item?.id ?? index}
                onClick={
                  menuRights?.canEdit === true
                    ? () => handleEdit(item)
                    : menuRights?.canView === true
                      ? () => handleRowClick(item)
                      : undefined
                }
                style={{
                  cursor:
                    menuRights?.canEdit === true || menuRights?.canView === true
                      ? "pointer"
                      : "default",
                }}
              >
                <div className="card-body">
                  <div className="approval-grid">
                    <div className="name-cell">
                      <span className="avatar" style={{ backgroundColor: getAvatarColor(index) }}>
                        {getInitials(item?.formName)}
                      </span>
                      <h4>{item?.formName}</h4>
                    </div>
                    <div className="levels-cell">{item?.noOfLevels}</div>
                    <div className="open-cell">
                      {menuRights?.canEdit === true && (
                        <i
                          className="far fa-edit edit-icon"
                          title="Edit"
                          onClick={(e) => handleEdit(item, e)}
                        ></i>
                      )}
                      {menuRights?.canView === true && (
                        <i
                          className="fas fa-chevron-right"
                          title="View"
                          style={{ cursor: "pointer", marginLeft: "12px" }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRowClick(item);
                          }}
                        ></i>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="card approval-row">
              <div className="card-body text-center py-4 text-muted">No approvals found</div>
            </div>
          )}
        </div>
      </div>


      <Modal show={show} onHide={handleClose} className="approvalLevelsModal" centered size="xl" dialogClassName="viewport-centered-modal-dialog">
        <Modal.Body>
          <div className="body" id="approvalLevelsModal">
            <div className="modal-header-bar">
              <h6>{selectedApproval?.formCode || selectedApproval?.formName}</h6>
              <i className="fas fa-times" onClick={handleClose}></i>
            </div>

            <div className="table-responsive mt-3">
              <table className="table table-striped table-borderless">
                <thead>
                  <tr>
                    <th>User Name</th>
                    <th>Level</th>
                    <th>Notifications</th>
                    <th>Mail</th>
                  </tr>
                </thead>
                <tbody>
                  {detailsLoading ? (
                    <tr>
                      <td><span className="skeleton-block skeleton-text" /></td>
                      <td><span className="skeleton-block skeleton-text short" /></td>
                      <td><span className="skeleton-block skeleton-checkbox" /></td>
                      <td><span className="skeleton-block skeleton-checkbox" /></td>
                    </tr>
                  ) : levelRows.length > 0 ? (
                    levelRows.map((row, idx) => (
                      <tr key={row.approverId ?? idx}>
                        <td>{row.approverName || "-"}</td>
                        <td>{row.levelNo ?? "-"}</td>
                        <td>
                          <input
                            type="checkbox"
                            checked={row.notifyInSystem}
                            onChange={(e) =>
                              setLevelRows((prev) =>
                                prev.map((r, i) => (i === idx ? { ...r, notifyInSystem: e.target.checked } : r))
                              )
                            }
                          />
                        </td>
                        <td>
                          <input
                            type="checkbox"
                            checked={row.notifyByEmail}
                            onChange={(e) =>
                              setLevelRows((prev) =>
                                prev.map((r, i) => (i === idx ? { ...r, notifyByEmail: e.target.checked } : r))
                              )
                            }
                          />
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={4} className="text-center text-muted">No approvers configured</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="back-btn">
              <button type="button" className="btn" onClick={handleClose}>
                Back
              </button>
            </div>
          </div>
        </Modal.Body>
      </Modal>
    </section>
  );
};

export default ApprovalMasterList;